import { useState, useCallback } from "react";
import { ComplianceTypeEntity } from "../../domain/entities/ComplianceTypeEntity";
import { ComplianceRepositoryImpl } from "../../data/repositories/ComplianceRepositoryImpl";
import {
  GetComplianceTypesUseCase,
  CreateComplianceTypeUseCase,
  UpdateComplianceTypeUseCase,
  DeleteComplianceTypeUseCase,
} from "../../domain/use-cases/compliance-types";

// Create repository instance (singleton)
const complianceRepository = new ComplianceRepositoryImpl();

// Create use cases
const getComplianceTypesUseCase = new GetComplianceTypesUseCase(complianceRepository);
const createComplianceTypeUseCase = new CreateComplianceTypeUseCase(complianceRepository);
const updateComplianceTypeUseCase = new UpdateComplianceTypeUseCase(complianceRepository);
const deleteComplianceTypeUseCase = new DeleteComplianceTypeUseCase(complianceRepository);

export interface UseComplianceTypesReturn {
  complianceTypes: ComplianceTypeEntity[];
  loading: boolean;
  error: Error | null;
  fetchComplianceTypes: () => Promise<void>;
  getComplianceType: (id: number) => Promise<ComplianceTypeEntity>;
  createComplianceType: (data: Partial<ComplianceTypeEntity>) => Promise<ComplianceTypeEntity>;
  updateComplianceType: (id: number, data: Partial<ComplianceTypeEntity>) => Promise<ComplianceTypeEntity>;
  deleteComplianceType: (id: number) => Promise<void>;
}

export const useComplianceTypes = (): UseComplianceTypesReturn => {
  const [complianceTypes, setComplianceTypes] = useState<ComplianceTypeEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchComplianceTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await getComplianceTypesUseCase.execute();
      setComplianceTypes(result);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch compliance types")
      );
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getComplianceType = useCallback(async (id: number) => {
    try {
      setError(null);
      return await complianceRepository.getComplianceTypeById(id);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to fetch compliance type")
      );
      throw err;
    }
  }, []);

  const createComplianceType = useCallback(
    async (data: Partial<ComplianceTypeEntity>) => {
      try {
        setError(null);
        const result = await createComplianceTypeUseCase.execute(data);
        // Refresh list
        await fetchComplianceTypes();
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to create compliance type")
        );
        throw err;
      }
    },
    [fetchComplianceTypes]
  );

  const updateComplianceType = useCallback(
    async (id: number, data: Partial<ComplianceTypeEntity>) => {
      try {
        setError(null);
        const result = await updateComplianceTypeUseCase.execute(id, data);
        // Refresh list
        await fetchComplianceTypes();
        return result;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to update compliance type")
        );
        throw err;
      }
    },
    [fetchComplianceTypes]
  );

  const deleteComplianceType = useCallback(
    async (id: number) => {
      try {
        setError(null);
        await deleteComplianceTypeUseCase.execute(id);
        // Refresh list
        await fetchComplianceTypes();
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error("Failed to delete compliance type")
        );
        throw err;
      }
    },
    [fetchComplianceTypes]
  );

  return {
    complianceTypes,
    loading,
    error,
    fetchComplianceTypes,
    getComplianceType,
    createComplianceType,
    updateComplianceType,
    deleteComplianceType,
  };
};

